import {
  makeStyles,
  mergeClasses,
  Persona,
  Tag,
  Tooltip,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FieldTypes } from "../models/FieldTypes";
import { UrlFieldValue } from "../models/FieldValues";
import { UserInformation } from "../models/UserInformation";
import { FlatVsaaTag, VsaaTag } from "../models/VsaaTag";
import { VsaComment } from "../models/VsaComment";
import { getClientAppNavUrl } from "../services/EndUserClientService";
import { SkanskaColorTokens } from "../style/colors";

interface DisplayFieldProps {
  type: FieldTypes;
  label: string;
  value?:
    | VsaaTag[]
    | string
    | undefined
    | boolean
    | null
    | string[]
    | number
    | UserInformation
    | UserInformation[]
    | VsaComment[]
    | UrlFieldValue;
  disableTextWrap?: boolean;
  showPath?: boolean;
  removed?: boolean;
  linkToClientApp?: boolean;
}

const useStyles = makeStyles({
  tagContainer: {
    display: "flex",
    gap: "5px",
    maxWidth: "100%",
    flexWrap: "wrap",
  },
  tagContainerNoWrap: {
    display: "flex",
    gap: "5px",
    maxWidth: "100%",
  },
  tag: {
    "> span": {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  removedTag: {
    textDecoration: "line-through",
  },
  avatar: {
    backgroundColor: "lightgrey",
    marginRight: "10px",
    borderRadius: "50%",
  },
  link: {
    color: SkanskaColorTokens.color_action_blue,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
});

export const DisplayField = (props: DisplayFieldProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    type,
    label,
    value,
    disableTextWrap,
    showPath,
    removed,
    linkToClientApp,
  } = props;

  const renderTagField = (fieldName: string, tags?: FlatVsaaTag[]) => {
    if (!tags) {
      return <></>;
    }

    return (
      <div
        className={
          disableTextWrap ? classes.tagContainerNoWrap : classes.tagContainer
        }
        aria-label={fieldName}
      >
        {tags.map((tag) => {
          const tagElement = (
            <Tag
              dismissible={false}
              shape="rounded"
              value={tag.id}
              size="extra-small"
              className={mergeClasses(
                classes.tag,
                removed && classes.removedTag
              )}
              aria-label={tag.title}
            >
              {showPath ? tag.path : tag.title}
            </Tag>
          );

          const wrappedTagElement = linkToClientApp ? (
            <a
              href={getClientAppNavUrl(tag.id)}
              className={classes.link}
              key={tag.id}
              target="_blank"
              rel="noopener noreferrer"
            >
              {tagElement}
            </a>
          ) : (
            <span key={tag.id}>{tagElement}</span>
          );

          return tag.path && tag.path.includes("/") ? (
            <Tooltip key={tag.id} content={tag.path} relationship="label">
              {wrappedTagElement}
            </Tooltip>
          ) : (
            wrappedTagElement
          );
        })}
      </div>
    );
  };

  const renderUserField = (
    fieldName: string,
    value?: UserInformation | UserInformation[]
  ) => {
    if (!value) {
      return <></>;
    }

    // Handle array case, take first user
    const user = Array.isArray(value) ? value[0] : value;

    if (!user) {
      return <></>;
    }

    return (
      <Persona
        textPosition="after"
        size="medium"
        name={user.displayName}
        textAlignment="center"
        aria-label={fieldName}
        avatar={{
          className: classes.avatar,
          image: {
            src: user.profilePhoto,
          },
        }}
      />
    );
  };

  const renderBooleanField = (fieldName: string, value?: boolean) => {
    if (value === undefined) {
      return <></>;
    }

    return (
      <div aria-label={fieldName}>
        {value ? t("booleanFieldValueTrue") : t("booleanFieldValueFalse")}
      </div>
    );
  };

  const renderDateField = (fieldName: string, value?: string) => {
    if (!value) {
      return <></>;
    }

    return (
      <span aria-label={fieldName}>
        {new Date(value).toLocaleString([], { hour12: false })}
      </span>
    );
  };

  const renderNumberField = (fieldName: string, value?: number) => {
    return (
      <span aria-label={fieldName}>{value !== undefined ? value : ""}</span>
    );
  };

  const renderTextField = (fieldName: string, value?: string) => {
    if (!value) {
      return <></>;
    }

    return <span aria-label={fieldName}>{value}</span>;
  };

  const renderCommentsField = (fieldName: string, value?: VsaComment[]) => {
    if (!value) {
      return <></>;
    }

    return (
      <span aria-label={fieldName}>{value ? value[0].message : null}</span>
    );
  };

  const renderUrlField = (fieldName: string, value?: UrlFieldValue) => {
    if (!value) {
      return <></>;
    }

    return (
      <Link aria-label={fieldName} to={value.url} className={classes.link}>
        {value.displayName}
      </Link>
    );
  };

  switch (type) {
    case FieldTypes.User:
      return renderUserField(
        label,
        value as UserInformation | UserInformation[]
      );
    case FieldTypes.Tag:
      return renderTagField(label, value as FlatVsaaTag[]);
    case FieldTypes.Date:
      return renderDateField(label, value as string);
    case FieldTypes.BooleanCheckBox:
      return renderBooleanField(label, value as boolean);
    case FieldTypes.BooleanSwitch:
      return renderBooleanField(label, value as boolean);
    case FieldTypes.Number:
      return renderNumberField(label, value as number);
    case FieldTypes.Text:
      return renderTextField(label, value as string);
    case FieldTypes.Comments:
      return renderCommentsField(label, value as VsaComment[]);
    case FieldTypes.Url:
      return renderUrlField(label, value as UrlFieldValue);
    default:
      throw Error("Invalid field type provided");
  }
};
